import { HAS_CHOSEN_ACCOUNT } from "@capone/common";
import { useCallback, useEffect, useState } from "react";

const MULTI_ACCOUNT_SWITCHER_EVENT = "corp:multi-account-switcher-updated";

const readStorageValue = (
  storage: Storage,
  key: string,
  fallbackValue: string
): string => {
  const currentValue = storage.getItem(key);

  if (currentValue) {
    return currentValue;
  }

  storage.setItem(key, fallbackValue);
  return fallbackValue;
};

const readHasSeenMultiAccountSwitcherModal = (): boolean => {
  try {
    const sessionValue = readStorageValue(
      sessionStorage,
      HAS_CHOSEN_ACCOUNT,
      "false"
    );
    const localValue = readStorageValue(
      localStorage,
      HAS_CHOSEN_ACCOUNT,
      sessionValue
    );

    return sessionValue === "true" || localValue === "true";
  } catch {
    return false;
  }
};

export const useShowMultiAccountSwitcherModal = () => {
  const [showMultiAccountsSwitcherModal, setShowMultiAccountsSwitcherModal] =
    useState(false);
  const [hasSeenMultiAccountsSwitcherModal, setHasSeenState] = useState(() =>
    readHasSeenMultiAccountSwitcherModal()
  );

  const setHasSeenMultiAccountsSwitcherModal = useCallback(
    (value: "true" | "false") => {
      try {
        localStorage.setItem(HAS_CHOSEN_ACCOUNT, value);
        sessionStorage.setItem(HAS_CHOSEN_ACCOUNT, value);
      } catch {
        console.warn("failed to set storage");
      }

      const nextValue = value === "true";
      setHasSeenState(nextValue);
      window.dispatchEvent(
        new CustomEvent(MULTI_ACCOUNT_SWITCHER_EVENT, {
          detail: nextValue,
        })
      );
    },
    []
  );

  useEffect(() => {
    const syncHasSeenState = () => {
      setHasSeenState(readHasSeenMultiAccountSwitcherModal());
    };

    const handleStorageUpdate = () => {
      syncHasSeenState();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === HAS_CHOSEN_ACCOUNT) {
        syncHasSeenState();
      }
    };

    // note: refreshing or closing a tab will clear local storage but not session storage, and both have to be false to show modal
    const handleBeforeUnload = () => {
      localStorage.removeItem(HAS_CHOSEN_ACCOUNT);
    };

    window.addEventListener(MULTI_ACCOUNT_SWITCHER_EVENT, handleStorageUpdate);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener(
        MULTI_ACCOUNT_SWITCHER_EVENT,
        handleStorageUpdate
      );
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return {
    showMultiAccountsSwitcherModal,
    setShowMultiAccountsSwitcherModal,
    hasSeenMultiAccountsSwitcherModal,
    setHasSeenMultiAccountsSwitcherModal,
  };
};
